import { Button, Center, Group, Loader, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { resolveBrandImageUrl } from '@/screens/ChannelRegistrationScreen/brandImageUrl'
import { ChannelBrandHeader, Shell } from '@/screens/ChannelRegistrationScreen/channelRegistrationShared'
import * as classes from '@/styles/SponsorSelectionScreen.css'
import { navigateTo } from '@/utils/navigation'

export function SignupChannelSelectionScreen() {
  const { api, configStore } = useGlobalState()
  const [selectedChannelId, setSelectedChannelId] = useState<string>('')

  const channelsQuery = useQuery({
    queryKey: ['self-registration', 'channels'],
    queryFn: () => api.getSelfRegistrationChannels(),
    retry: false,
  })

  const sponsorIds = channelsQuery.data?.sponsorIds ?? []

  const channelQueries = useQueries({
    queries: sponsorIds.map(id => ({
      queryKey: ['signup-channel', id],
      queryFn: async () => {
        const channel = await api.getChannelPublicInfo(id)
        // /channel_info returns a full-URL brand image usable on a logged-out page; logo is optional.
        const info = await api.safeRequest(api.getChannelInfo, id)
        const brandImageUrl = resolveBrandImageUrl(
          info.ok ? info.data.brandImage : channel.brandImage,
          configStore.serverBaseUrl,
        )
        return { channel, brandImageUrl }
      },
      retry: false,
    })),
  })

  const isInfoLoading = channelQueries.some(query => query.isPending)

  const displayChannels = channelQueries
    .flatMap(query => (query.data ? [query.data] : []))
    .filter(({ channel }) => channel.selfRegistrationEnabled && channel.active)

  const handleNext = () => {
    if (!selectedChannelId) return
    navigateTo('channelRegistration', { channelId: selectedChannelId })
  }

  const renderBody = () => {
    if (channelsQuery.isLoading || (sponsorIds.length > 0 && isInfoLoading)) {
      return (
        <Center mih={240}>
          <Loader />
        </Center>
      )
    }

    if (channelsQuery.isError) {
      return (
        <Stack align="center" gap="xs" py="xl" ta="center">
          <Text size="sm" c="red">
            No channels available for registration
          </Text>
          <Text size="xs" c="dimmed">
            Please try again or contact support.
          </Text>
        </Stack>
      )
    }

    if (displayChannels.length === 0) {
      return (
        <Stack align="center" gap="xs" py="xl" ta="center">
          <Text size="sm" c="dimmed">
            There are no channels available for self-registration right now.
          </Text>
        </Stack>
      )
    }

    return (
      <Stack gap="xs">
        {displayChannels.map(({ channel, brandImageUrl }) => {
          const isSelected = selectedChannelId === channel.channelId
          return (
            <UnstyledButton
              key={channel.channelId}
              onClick={() => setSelectedChannelId(channel.channelId)}
              className={theme.cx(classes.sponsorCard, isSelected && classes.sponsorCardSelected)}>
              <ChannelBrandHeader channel={channel} imageUrl={brandImageUrl} />
            </UnstyledButton>
          )
        })}
      </Stack>
    )
  }

  return (
    <Shell srHeading="Sign up for a Sidekick channel">
      <Stack gap="lg" mt="xl" maw={560} mx="auto">
        <Title order={4} ta="center">
          Select a Channel
        </Title>

        {renderBody()}

        <Group justify="flex-end" mt="xl">
          <Button onClick={handleNext} disabled={!selectedChannelId}>
            Next
          </Button>
        </Group>
      </Stack>
    </Shell>
  )
}
